import { NOTIFICATION_GROUPS, NOTIFICATION_SEVERITIES, muteLabel, type CategoryPrefDTO, type NotificationGroup, type NotificationSeverity } from '@devrobot/shared';
import { BellOff, BellRing, Lock, MessageSquare, Smartphone, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { useEffect, useState } from 'react';
import {
  useForgetDevice,
  useLinkRocketChat,
  useNotificationPrefs,
  usePushDevices,
  useResumeProject,
  useRocketChat,
  useTestPush,
  useUnlinkRocketChat,
  useUpdatePrefs,
  useVerifyRocketChat,
} from '../../api/notifications';
import { disablePush, enablePush, pushState, type PushState } from '../../kit/push';
import { useToast } from '../../components/Toaster';
import { ErrorAlert } from '../../components/ErrorAlert';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonLines } from '../../components/Skeleton';

const GROUP_LABEL: Record<NotificationGroup, { title: string; description: string }> = {
  delivery: { title: 'Deployments and operations', description: 'What happens when you ship, and when an operation does not finish cleanly.' },
  infrastructure: { title: 'Infrastructure', description: 'Application health, agent connectivity, domains and disk space.' },
  data: { title: 'Data safety', description: 'Backups, restores and data migrations.' },
  security: { title: 'Security and governance', description: 'Access changes, privileged shells and platform settings.' },
};

const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  info: 'Everything',
  warning: 'Warnings and problems',
  critical: 'Only critical',
};

/**
 * The category x channel matrix.
 *
 * Push and Rocket.Chat columns are shown but disabled until those channels exist on this
 * server, so the shape of the screen does not change when they are switched on later.
 */
export function NotificationSettings() {
  const prefs = useNotificationPrefs();
  const update = useUpdatePrefs();
  const resume = useResumeProject();

  if (prefs.isPending) return <SkeletonLines lines={8} />;
  if (prefs.error) return <ErrorAlert error={prefs.error} />;
  if (!prefs.data) return null;

  const byGroup = NOTIFICATION_GROUPS.map((group) => ({ group, rows: prefs.data.categories.filter((c) => c.group === group) })).filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-8">
      {update.error ? <ErrorAlert error={update.error} /> : null}

      <PushSection />
      <RocketChatSection />

      {byGroup.map(({ group, rows }) => (
        <section key={group} className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-10">
          <div>
            <h2 className="font-semibold">{GROUP_LABEL[group].title}</h2>
            <p className="mt-1 text-sm text-base-content/60">{GROUP_LABEL[group].description}</p>
          </div>
          <div className="card dr-surface min-w-0">
            <div className="card-body gap-0 p-5 sm:p-6">
              {rows.map((row) => (
                <CategoryRow key={row.category} row={row} onChange={(patch) => update.mutate({ [row.category]: patch })} busy={update.isPending} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-10">
        <div>
          <h2 className="font-semibold">Silenced projects</h2>
          <p className="mt-1 text-sm text-base-content/60">
            Silence a project from its own page. Notifications still reach your inbox while it is silenced &mdash; only push and chat go quiet.
          </p>
        </div>
        <div className="card dr-surface min-w-0">
          <div className="card-body gap-3 p-5 sm:p-6">
            {prefs.data.mutes.length === 0 ? (
              <p className="text-sm text-base-content/60">Nothing is silenced.</p>
            ) : (
              <ul className="divide-y divide-hairline">
                {prefs.data.mutes.map((m) => (
                  <li key={m.projectKey} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <BellOff className="size-4 shrink-0 text-base-content/50" aria-hidden="true" />
                        {m.projectKey === '*' ? 'Every project' : <Link to={`/projects/${m.projectKey}`} className="hover:underline">{m.projectKey}</Link>}
                      </span>
                      <span className="block text-sm text-base-content/60">
                        {m.until ? <>Until <RelativeTime value={m.until} /></> : 'Until you turn it back on'}
                      </span>
                    </span>
                    <button type="button" className="btn btn-sm" disabled={resume.isPending} onClick={() => resume.mutate(m.projectKey)}>
                      Turn back on
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function CategoryRow({ row, onChange, busy }: { row: CategoryPrefDTO; onChange: (patch: { inapp?: boolean; push?: boolean; rocketchat?: boolean; minSeverity?: NotificationSeverity }) => void; busy: boolean }) {
  return (
    <div className="grid gap-3 border-b border-hairline py-4 first:pt-0 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
      <div className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium">
          {row.label}
          {row.mandatory && (
            <span className="badge badge-ghost badge-xs gap-1" title="This one always stays on">
              <Lock className="size-2.5" aria-hidden="true" />
              Always on
            </span>
          )}
        </span>
        <span className="block text-sm text-base-content/60">{row.description}</span>
        {row.mandatory && <span className="mt-1 block text-xs text-base-content/50">It tells you when your own access changes, so it cannot be switched off.</span>}
      </div>

      <div className="flex flex-wrap items-center gap-4 sm:justify-end">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={row.inapp}
            disabled={row.mandatory || busy}
            onChange={(e) => onChange({ inapp: e.target.checked })}
          />
          <span className="text-base-content/70">Inbox</span>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <select
            className="select select-xs w-auto"
            value={row.minSeverity}
            disabled={row.mandatory || busy}
            onChange={(e) => onChange({ minSeverity: e.target.value as NotificationSeverity })}
            aria-label={`How much to send for ${row.label}`}
          >
            {NOTIFICATION_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/** Re-exported for the mute list so the settings page and the silence menu agree on wording. */
export { muteLabel };

/* --------------------------------------------------------------- web push */

/**
 * What each browser state means for the person reading it. The unsupported and
 * needs-install cases are the ones that actually happen: on iPhone, push only works once
 * the app is on the Home Screen, and nothing in the UI hints at that unless it is said.
 */
const PUSH_COPY: Record<PushState, { title: string; detail: string; action?: 'enable' | 'disable' }> = {
  unsupported: { title: 'Not available in this browser', detail: 'This browser cannot receive push notifications. Chrome, Edge, Firefox and Safari 16.4 or later all can.' },
  insecure: { title: 'Needs a secure connection', detail: 'Push notifications require https. Ask a platform administrator to serve DevRobot over https.' },
  'needs-install': {
    title: 'Add DevRobot to your Home Screen first',
    detail: 'On iPhone and iPad, notifications only work from the installed app. Tap Share, then Add to Home Screen, and turn this on from there.',
  },
  default: { title: 'Notifications are off on this device', detail: 'You will be asked to allow notifications.', action: 'enable' },
  denied: { title: 'Notifications are blocked', detail: 'Allow them in your browser or system settings for DevRobot, then come back. On iPhone: Settings, Notifications, DevRobot.' },
  granted: { title: 'Allowed, but this device is not registered', detail: 'Turn it on to start receiving notifications here.', action: 'enable' },
  subscribed: { title: 'Notifications are on for this device', detail: 'Alerts arrive even when DevRobot is closed.', action: 'disable' },
};

function PushSection() {
  const [state, setState] = useState<PushState>();
  const [busy, setBusy] = useState(false);
  const devices = usePushDevices();
  const forget = useForgetDevice();
  const test = useTestPush();
  const toast = useToast();

  useEffect(() => {
    void pushState().then(setState);
  }, []);

  const copy = state ? PUSH_COPY[state] : undefined;

  const toggle = async () => {
    setBusy(true);
    try {
      if (copy?.action === 'disable') {
        await disablePush();
        setState(await pushState());
      } else {
        const next = await enablePush();
        setState(next);
        if (next === 'denied') toast.error('Notifications were blocked', { description: 'Allow them in your browser settings, then try again.' });
      }
      await devices.refetch();
    } catch (err) {
      toast.error('Could not change notifications on this device', { description: (err as Error)?.message });
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    const result = await test.mutateAsync();
    if (result.sent > 0) toast.success(`Test sent to ${result.sent} device${result.sent === 1 ? '' : 's'}`, { description: 'It should arrive in a moment.' });
    else toast.error('No device accepted the test', { description: result.failed[0]?.error ?? 'Try turning notifications off and on again.' });
  };

  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-10">
      <div>
        <h2 className="font-semibold">Push notifications</h2>
        <p className="mt-1 text-sm text-base-content/60">Alerts on your phone or computer, even when DevRobot is closed. Turned on per device.</p>
      </div>
      <div className="card dr-surface min-w-0">
        <div className="card-body gap-5 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <span className="min-w-0">
              <span className="block text-sm font-medium">{copy?.title ?? 'Checking this device…'}</span>
              <span className="block text-sm text-base-content/60">{copy?.detail ?? ''}</span>
            </span>
            {copy?.action ? (
              <input
                type="checkbox"
                className="toggle toggle-primary shrink-0"
                checked={copy.action === 'disable'}
                disabled={busy}
                onChange={() => void toggle()}
                aria-label="Notifications on this device"
              />
            ) : null}
          </div>

          {state === 'subscribed' && (
            <div>
              <button type="button" className="btn btn-sm" onClick={() => void sendTest()} disabled={test.isPending}>
                <BellRing className="size-4" aria-hidden="true" />
                Send a test notification
              </button>
            </div>
          )}

          {devices.data && devices.data.length > 0 && (
            <div>
              <h3 className="mb-1 text-sm font-medium">Your devices</h3>
              <ul className="divide-y divide-hairline">
                {devices.data.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-4 py-3">
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Smartphone className="size-4 shrink-0 text-base-content/50" aria-hidden="true" />
                        {d.label}
                        {d.unreachable && (
                          <span className="badge badge-warning badge-soft badge-xs gap-1">
                            <TriangleAlert className="size-2.5" aria-hidden="true" />
                            Unreachable
                          </span>
                        )}
                      </span>
                      <span className="block text-sm text-base-content/60">
                        {d.unreachable ? (
                          'This device has not accepted notifications recently. Remove it, then turn notifications on again there.'
                        ) : (
                          <>Added <RelativeTime value={d.createdAt} /></>
                        )}
                      </span>
                    </span>
                    <button type="button" className="btn btn-sm" disabled={forget.isPending} onClick={() => forget.mutate(d.id)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- rocket.chat */

/**
 * Linking a Rocket.Chat account.
 *
 * The username has to be proved, not just typed: the bot sends a code and it has to come
 * back. Otherwise anyone could point their own notifications at a colleague's messages.
 */
function RocketChatSection() {
  const link = useRocketChat();
  const start = useLinkRocketChat();
  const verify = useVerifyRocketChat();
  const unlink = useUnlinkRocketChat();
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const toast = useToast();

  if (link.isPending || !link.data) return null;
  // Nothing to offer until a platform administrator has connected a server.
  if (!link.data.configured) return null;

  const state = link.data;

  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-10">
      <div>
        <h2 className="font-semibold">Rocket.Chat</h2>
        <p className="mt-1 text-sm text-base-content/60">
          Get notifications as a direct message. Link your account once, then switch on the categories you want above.
        </p>
      </div>
      <div className="card dr-surface min-w-0">
        <div className="card-body gap-4 p-5 sm:p-6">
          {state.verified ? (
            <div className="flex items-center justify-between gap-4">
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <MessageSquare className="size-4 shrink-0 text-base-content/50" aria-hidden="true" />
                  Linked to @{state.username}
                </span>
                <span className="block text-sm text-base-content/60">Direct messages from the DevRobot bot.</span>
              </span>
              <button type="button" className="btn btn-sm" disabled={unlink.isPending} onClick={() => unlink.mutate(undefined)}>
                Unlink
              </button>
            </div>
          ) : state.pending ? (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                verify.mutate(code, {
                  onSuccess: () => {
                    setCode('');
                    toast.success('Rocket.Chat linked', { description: 'Turn on the categories you want to receive there.' });
                  },
                });
              }}
            >
              <fieldset className="fieldset min-w-0 flex-1">
                <legend className="fieldset-legend">Code sent to @{state.username}</legend>
                <input className="input w-full" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-EFGH" autoComplete="one-time-code" required />
                <p className="label">The bot sent you a code. It expires in 10 minutes.</p>
              </fieldset>
              <button type="submit" className="btn btn-primary btn-sm" disabled={verify.isPending || !code.trim()}>
                Confirm
              </button>
            </form>
          ) : (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                start.mutate(username.trim());
              }}
            >
              <fieldset className="fieldset min-w-0 flex-1">
                <legend className="fieldset-legend">Your Rocket.Chat username</legend>
                <input className="input w-full" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your.name" required />
                <p className="label">The bot will send you a code to confirm it is you.</p>
              </fieldset>
              <button type="submit" className="btn btn-primary btn-sm" disabled={start.isPending || !username.trim()}>
                Send code
              </button>
            </form>
          )}
          {start.error ? <ErrorAlert error={start.error} /> : null}
          {verify.error ? <ErrorAlert error={verify.error} /> : null}
        </div>
      </div>
    </section>
  );
}
